/* eslint-disable react/prop-types */

import React, { useState, useEffect } from 'react';
import { Table, Form } from 'react-bootstrap';
import { FormLabel } from './FormLabel';
import ErrorFeedback from './ErrorFeedback';

const ScreenPermissionsTable = ({
  metadata,
  data,
  field,
  errors,
  onChange
}) => {
  const [permissionsData, setPermissionsData] = useState([]);
  const [allScreensData, setAllScreensData] = useState([]);

  // Helper function to get action value by name
  const getActionValue = (actions, actionName) => {
    const action = actions.find(a => a.name === actionName);
    return action ? action.value === 'true' || action.value === true : false;
  };

  // Function to create submission data
  const createSubmissionData = (displayData, sourceScreens) => {
    const submissionData = [];

    displayData.forEach(displayItem => {
      // Find all screens with the same name
      const matchingScreens = sourceScreens.filter(
        screen => screen.screen === displayItem.Screen
      );

      // Create permission entry for each matching screen
      matchingScreens.forEach(screen => {
        const updatedActions = screen.action.map(act => {
          let value = 'false';

          if (act.name === 'View') {
            value = 'true';
          } else if (act.name === 'Edit') {
            value = displayItem.Edit === true ? 'true' : 'false';
          } else if (act.name === 'Create') {
            value = displayItem.Create === true ? 'true' : 'false';
          }

          return {
            id: act.id,
            name: act.name,
            value: value
          };
        });

        submissionData.push({
          id: screen.id,
          screen: screen.screen,
          module_id: screen.module_id,
          action: updatedActions
        });
      });
    });

    return submissionData;
  };

  // Initialize permissions data when data prop changes
  useEffect(() => {
    let screenData = null;

    if (data && data.data && Array.isArray(data.data)) {
      screenData = data.data;
    } else if (Array.isArray(data)) {
      screenData = data;
    }

    if (screenData && screenData.length > 0) {
      // Store all screens for later submission
      setAllScreensData(screenData);

      // Transform the data structure
      const transformedData = screenData.map(item => ({
        id: item.id,
        Screen: item.screen,
        module_id: item.module_id,
        View: getActionValue(item.action, 'View'),
        Edit: getActionValue(item.action, 'Edit'),
        Create: getActionValue(item.action, 'Create'),
        originalIds: [item.id]
      }));

      // Group screens by name
      const uniqueScreensMap = new Map();

      transformedData.forEach(item => {
        const screenName = item.Screen;
        if (!uniqueScreensMap.has(screenName)) {
          uniqueScreensMap.set(screenName, {
            ...item,
            originalIds: [item.id],
            View: true, // Always true
            Edit: item.Edit, // Use the actual value from data
            Create: item.Create // Use the actual value from data
          });
        } else {
          const existingScreen = uniqueScreensMap.get(screenName);
          existingScreen.originalIds.push(item.id);
          // If any instance has Edit/Create as true, set it to true
          existingScreen.Edit = existingScreen.Edit || item.Edit;
          existingScreen.Create = existingScreen.Create || item.Create;
        }
      });

      const uniqueScreensArray = Array.from(uniqueScreensMap.values());

      setPermissionsData(uniqueScreensArray);

      // Always update form field with current data from API
      const submissionData = createSubmissionData(
        uniqueScreensArray,
        screenData
      );
      onChange(submissionData);
    }
  }, [data]);

  const handlePermissionChange = (screenName, permission, checked) => {
    const updatedData = permissionsData.map(item => {
      if (item.Screen === screenName) {
        return {
          ...item,
          [permission]: checked
        };
      }
      return item;
    });

    setPermissionsData(updatedData);

    // Create submission data using allScreensData
    const submissionData = createSubmissionData(updatedData, allScreensData);
    onChange(submissionData);
  };

  // Check if we have valid data
  let screenData = null;
  if (data && data.data && Array.isArray(data.data)) {
    screenData = data.data;
  } else if (Array.isArray(data)) {
    screenData = data;
  }

  if (!screenData || screenData.length === 0) {
    return (
      <div className="mb-3">
        <FormLabel
          info_key={metadata.info_key}
          label={metadata.label}
          required={metadata?.validation?.required}
        />
        <div className="text-muted">No screens available</div>
        <ErrorFeedback error={errors[metadata.id]} istextAndUnit={false} />
      </div>
    );
  }

  return (
    <div className="mb-3">
      <FormLabel
        info_key={metadata.info_key}
        label={metadata.label}
        required={metadata?.validation?.required}
      />

      <div className="table-responsive">
        <Table striped bordered hover size="sm">
          <thead className="table-dark">
            <tr>
              <th style={{ width: '40%' }}>Screen Name</th>
              <th style={{ width: '20%' }}>View</th>
              <th style={{ width: '20%' }}>Edit</th>
              <th style={{ width: '20%' }}>Create</th>
            </tr>
          </thead>
          <tbody>
            {permissionsData.map(screen => (
              <tr key={screen.Screen}>
                <td>
                  <strong>{screen.Screen}</strong>
                  {screen.originalIds?.length > 1 && (
                    <div className="text-muted small">
                      ({screen.originalIds.length} screens)
                    </div>
                  )}
                </td>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={true}
                    disabled={true}
                    title="View permission is always enabled"
                  />
                </td>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={screen.Edit || false}
                    onChange={e =>
                      handlePermissionChange(
                        screen.Screen,
                        'Edit',
                        e.target.checked
                      )
                    }
                  />
                </td>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={screen.Create || false}
                    onChange={e =>
                      handlePermissionChange(
                        screen.Screen,
                        'Create',
                        e.target.checked
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <ErrorFeedback error={errors[metadata.id]} istextAndUnit={false} />

      <div className="mt-2 text-muted small">
        Unique screens: {permissionsData.length}
        {allScreensData.length > permissionsData.length && (
          <span> (Total screens: {allScreensData.length})</span>
        )}
      </div>
    </div>
  );
};

export default ScreenPermissionsTable;
